import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../../i18n/LanguageContext';
import { AuctionIcon } from '../../utils/IconWrapper';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://geauction.com/wp-content/uploads/2018/06/Your-First-Auction-What-You-Need-to-Know.jpg');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const ServiceTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 60px;
    height: 3px;
    background-color: var(--accent-primary);
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 2rem 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  svg {
    margin-top: 5px;
    margin-right: 1rem;
    min-width: 24px;
    color: var(--accent-primary);
  }
`;

const FeatureText = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.2rem;
  }
  
  p {
    color: var(--text-secondary);
    line-height: 1.6;
  }
`;

const CallToAction = styled.div`
  background-color: var(--bg-secondary);
  padding: 3rem;
  text-align: center;
  border-radius: 8px;
  margin: 3rem 0;
`;

const CTATitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const CTAText = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Auctions: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Aste Immobiliari | Servizi J.O.H.' 
            : 'Real Estate Auctions | J.O.H. Services'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri il nostro servizio di assistenza e consulenza per aste immobiliari: accedi a opportunità esclusive con il supporto di esperti del settore.' 
            : 'Discover our assistance and consulting service for real estate auctions: access exclusive opportunities with the support of industry experts.'} 
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Aste Immobiliari' : 'Real Estate Auctions'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Assistenza professionale per accedere alle opportunità del mercato delle aste immobiliari.'
              : 'Professional assistance to access opportunities in the real estate auction market.'}
          </HeroSubtitle>
          <Button variant="primary" to="/contact">
            {language === 'it' ? 'Richiedi Informazioni' : 'Request Information'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="6rem 0">
        <Content>
          <ServiceTitle>
            {language === 'it' ? 'Il Nostro Servizio di Assistenza per Aste' : 'Our Auction Assistance Service'}
          </ServiceTitle>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Il nostro team di esperti ti guida attraverso il complesso mondo delle aste immobiliari. Forniamo consulenza pre-asta, assistenza durante la procedura e supporto post-aggiudicazione, permettendoti di accedere a opportunità immobiliari esclusive a prezzi vantaggiosi.'
              : 'Our team of experts guides you through the complex world of real estate auctions. We provide pre-auction consulting, assistance during the procedure, and post-award support, allowing you to access exclusive real estate opportunities at advantageous prices.'}
          </ServiceDescription>
          
          <ServiceDescription>
            {language === 'it'
              ? 'Le aste immobiliari rappresentano una grande opportunità per acquistare proprietà di valore a prezzi competitivi, ma richiedono conoscenze specifiche e una corretta assistenza professionale per evitare rischi e cogliere le migliori occasioni.'
              : 'Real estate auctions represent a great opportunity to purchase valuable properties at competitive prices, but they require specific knowledge and proper professional assistance to avoid risks and seize the best opportunities.'}
          </ServiceDescription>
          
          <FeatureList>
            <FeatureItem>
              <AuctionIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Consulenza Pre-Asta' : 'Pre-Auction Consulting'}</h3>
                <p>
                  {language === 'it'
                    ? 'Analizziamo la documentazione relativa all\'immobile, valutiamo il suo valore di mercato e identifichiamo eventuali criticità, offrendoti un quadro completo prima della partecipazione.'
                    : 'We analyze the documentation related to the property, assess its market value, and identify any critical issues, offering you a complete picture before participation.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <AuctionIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Assistenza Durante l\'Asta' : 'Assistance During the Auction'}</h3>
                <p>
                  {language === 'it'
                    ? 'Ti rappresentiamo durante la procedura d\'asta, gestendo le offerte secondo la strategia concordata e massimizzando le tue possibilità di aggiudicazione.'
                    : 'We represent you during the auction procedure, managing bids according to the agreed strategy and maximizing your chances of winning.'}
                </p>
              </FeatureText>
            </FeatureItem>
            
            <FeatureItem>
              <AuctionIcon />
              <FeatureText>
                <h3>{language === 'it' ? 'Supporto Post-Aggiudicazione' : 'Post-Award Support'}</h3>
                <p>
                  {language === 'it'
                    ? 'Ci occupiamo di tutte le pratiche necessarie dopo l\'aggiudicazione, dall\'assistenza notarile al trasferimento di proprietà, fino alla presa in possesso dell\'immobile.'
                    : 'We take care of all necessary procedures after the award, from notarial assistance to property transfer, up to taking possession of the property.'}
                </p>
              </FeatureText>
            </FeatureItem>
          </FeatureList>
          
          <CallToAction>
            <CTATitle>
              {language === 'it' ? 'Interessato alle Aste Immobiliari?' : 'Interested in Real Estate Auctions?'}
            </CTATitle>
            <CTAText>
              {language === 'it'
                ? 'Contattaci oggi per una consulenza personalizzata e scopri come possiamo aiutarti ad accedere alle migliori opportunità del mercato delle aste immobiliari.'
                : 'Contact us today for personalized consulting and discover how we can help you access the best opportunities in the real estate auction market.'}
            </CTAText>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci Ora' : 'Contact Us Now'}
            </Button>
          </CallToAction>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default Auctions; 
